import React, { useEffect, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';
import { mapValues } from 'lodash';

import { useOnceReveal } from '@common/hooks';
import { SizeInfoAction, useSizeInfoChannelListener } from '@widgets/size-broadcast-channel';
import { showToast, useIsAuthenticated } from '@domains/pdp-common';
import {
  InternationalSizeGuideContainer,
  PDP_INTEGRATED_DATA_QUERY_KEY,
  useCatalogProductIdParams,
  useGetPdpIntegratedData,
} from '@domains/pdp-common';

import { ModelSizeContainer } from '../../model-size/containers/ModelSizeContainer';
import { SizeRecommendationSectionContainer } from '../../size-recommendation/containers/SizeRecommendationSectionContainer';
import { SetUserAccountBodyInput } from '../../size-recommendation/graphql/user-body-input_types.2';
import { useSetUserAccountBodyMutation } from '../../size-recommendation/queries/useSetUserAccountBodyMutation';
import { UserAccountModel } from '../../size-recommendation/utils/types';
import { useTabsService } from '../../tabs/services/useTabsService';
import { useSizeTabABT } from '../abt';
import { LegacySizeInfoContainer } from '../abt/legacy';
import { OverseasDirectPurchaseSizeGuide } from '../components/overseas-direct-purchase/OverseasDirectPurchaseSizeGuide';
import { getShouldOpenPopup, removeShouldOpenPopup } from '../constant/local-storage';
import { useSizeInfoContainerUBL } from '../log/useSizeInfoContainerUBL';
import { GET_USER_ACCOUNT_BODY_QUERY_KEY, useGetUserAccountBodyQuery } from '../queries/useGetUserAccountBodyQuery';
import { usePendingSizeInput } from '../queries/usePendingSizeInputQuery';
import { useSizeInfoAgreementListener } from '../services/useSizeInfoAgreementListener';
import {
  useGetUserAccountProductMeasurementListQuery,
  USER_ACCOUNT_PRODUCT_MEASUREMENT_LIST_QUERY_KEY,
} from '../services/useSizeInfoMutation';
import { useSizeInfoService } from '../services/useSizeInfoService';
import { useAgreementPopup } from '../utils/agreement-popup';
import { ActualSizeContainer } from './ActualSizeContainer';
import { AiCropSizeContainer } from './AiCropSizeContainer';

export interface SizeInfoContainerProps {
  unregisteredView?: React.ReactNode | ((props: { userAccount: UserAccountModel | null }) => React.ReactNode);
}

export const SizeInfoContainer: React.FC<React.PropsWithChildren<SizeInfoContainerProps>> = () => {
  const { isControlGroup, isLoading } = useSizeTabABT();

  if (isLoading) {
    return null;
  }

  if (isControlGroup) {
    return <LegacySizeInfoContainer />;
  } else {
    return <NewSizeInfoContainer />;
  }
};

const NewSizeInfoContainer: React.FC = () => {
  const { hideActualSize, hideAiCropSize, hideSizeGuide, hideOverseas, active_tab, tab_list, table_list, onClickTab } =
    useSizeInfoService();
  const { onSizeRecommendRefChange } = useTabsService();
  const { subtabSizeImpressionUBL } = useSizeInfoContainerUBL();
  const { targetRef, isRevealed } = useOnceReveal();
  const queryClient = useQueryClient();
  const { data: pendingData, isLoading: isPendingLoading, consumePending, setPending } = usePendingSizeInput();
  const { catalog_product_id } = useCatalogProductIdParams();
  const { fitting_model_list } = useGetPdpIntegratedData({ catalog_product_id });
  const hasModelInfo = Boolean(fitting_model_list?.length);
  const { isLoggedIn: isAuthenticated, isLoading: isAuthLoading } = useIsAuthenticated();
  const { data: userAccountBodyData, isLoading: isLoadingUserBody } = useGetUserAccountBodyQuery();
  const { mutateAsync: setUserAccountBody } = useSetUserAccountBodyMutation();
  const { openSizeRecommendationPopup } = useAgreementPopup(catalog_product_id);

  const hasBody = userAccountBodyData?.user_account?.has_body ?? false;
  const autoSaveExecutedRef = useRef(false);

  const { data: userMeasurementData, isLoading: isLoadingUserMeasurement } =
    useGetUserAccountProductMeasurementListQuery({ enabled: isAuthenticated });
  const isUserSizeAgreed =
    userMeasurementData?.data?.user_account_product_measurement_list?.is_size_info_agreed ?? false;

  useEffect(() => {
    if (isRevealed) subtabSizeImpressionUBL();
  }, [isRevealed, subtabSizeImpressionUBL]);

  useEffect(() => {
    const shouldShowPopup =
      !isPendingLoading &&
      isAuthenticated &&
      !isAuthLoading &&
      !isLoadingUserBody &&
      !isLoadingUserMeasurement &&
      !hasBody &&
      !isUserSizeAgreed &&
      getShouldOpenPopup() &&
      !!pendingData;

    if (shouldShowPopup) {
      removeShouldOpenPopup();
      openSizeRecommendationPopup();
    }
  }, [
    isPendingLoading,
    pendingData,
    isAuthenticated,
    isAuthLoading,
    hasBody,
    isLoadingUserBody,
    isLoadingUserMeasurement,
    isUserSizeAgreed,
    openSizeRecommendationPopup,
  ]);

  useEffect(() => {
    const autoSave = async () => {
      if (
        isPendingLoading ||
        isLoadingUserBody ||
        isAuthLoading ||
        isLoadingUserMeasurement ||
        autoSaveExecutedRef.current
      )
        return;
      if (!isAuthenticated) return;
      if (!hasBody && !isUserSizeAgreed) return;

      const pendingValues = consumePending();
      if (!pendingValues) return;

      autoSaveExecutedRef.current = true;
      try {
        await setUserAccountBody({
          variables: { input: mapValues(pendingValues, Number) as unknown as SetUserAccountBodyInput },
        });
        await queryClient.refetchQueries(GET_USER_ACCOUNT_BODY_QUERY_KEY);
        await queryClient.refetchQueries([PDP_INTEGRATED_DATA_QUERY_KEY, catalog_product_id]);
        showToast('체형 정보가 저장되었습니다.');
      } catch {
        showToast('체형 정보 저장에 실패했습니다.');
        autoSaveExecutedRef.current = false;
      }
    };
    autoSave();
  }, [
    isPendingLoading,
    isAuthenticated,
    isAuthLoading,
    hasBody,
    isLoadingUserBody,
    isLoadingUserMeasurement,
    isUserSizeAgreed,
    catalog_product_id,
    setUserAccountBody,
    queryClient,
    consumePending,
    setPending,
  ]);

  useSizeInfoAgreementListener({
    onAgree: async (payload) => {
      if (payload.type !== 'recommend') return;

      const pendingValues = consumePending();
      if (!pendingValues) return;

      try {
        await setUserAccountBody({
          variables: { input: mapValues(pendingValues, Number) as unknown as SetUserAccountBodyInput },
        });
        await queryClient.refetchQueries(GET_USER_ACCOUNT_BODY_QUERY_KEY);
        await queryClient.refetchQueries([PDP_INTEGRATED_DATA_QUERY_KEY, catalog_product_id]);
        showToast('체형 정보가 저장되었습니다.');
      } catch {
        showToast('체형 정보 저장에 실패했습니다.');
      }
    },
    onSelectMyPurchase: () => {},
    onCompareDirectInput: () => {
      queryClient.invalidateQueries(GET_USER_ACCOUNT_BODY_QUERY_KEY);
      queryClient.invalidateQueries([USER_ACCOUNT_PRODUCT_MEASUREMENT_LIST_QUERY_KEY]);
    },
  });

  useSizeInfoChannelListener((message) => {
    if (message.action === SizeInfoAction.BODY_PROFILE_UPDATED) {
      queryClient.refetchQueries(GET_USER_ACCOUNT_BODY_QUERY_KEY);
      queryClient.refetchQueries([PDP_INTEGRATED_DATA_QUERY_KEY, catalog_product_id]);
      queryClient.refetchQueries([USER_ACCOUNT_PRODUCT_MEASUREMENT_LIST_QUERY_KEY]);
    }

    if (message.action === SizeInfoAction.COMPARE_DIRECT_INPUT) {
      queryClient.invalidateQueries(GET_USER_ACCOUNT_BODY_QUERY_KEY);
      queryClient.invalidateQueries([USER_ACCOUNT_PRODUCT_MEASUREMENT_LIST_QUERY_KEY]);
    }
  });

  return (
    <Container ref={targetRef}>
      <SizeRecommendationSectionContainer onSizeRecommendRefChange={onSizeRecommendRefChange} />
      {!hideActualSize && <ActualSizeContainer />}
      {!hideAiCropSize && <AiCropSizeContainer hasModelInfo={hasModelInfo} />}
      {!hideOverseas && (
        <OverseasDirectPurchaseSizeGuide
          active_tab={active_tab}
          tab_list={tab_list}
          table_list={table_list}
          onClickTab={onClickTab}
        />
      )}
      {!hideSizeGuide && (
        <StyledSizeGuide>
          <InternationalSizeGuideContainer />
        </StyledSizeGuide>
      )}
      <ModelSizeContainer />
    </Container>
  );
};

const Container = styled.div`
  margin-bottom: 64px;
  background-color: ${vars.color.backgroundBase};
  touch-action: pan-y;
`;

const StyledSizeGuide = styled.div`
  padding: 20px 16px 40px;
  background-color: ${vars.color.white};
`;
